import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';
import EarlyBirdBadge from '../components/EarlyBirdBadge';
import CabinScarcity from '../components/CabinScarcity';
import CruiseHeroCarousel from '../components/cruise/CruiseHeroCarousel';
import { buildCruiseGallery } from '../data/cruiseGalleries';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, Calendar, Users, ArrowRight, Ship, Anchor, 
  Check, X, ChevronRight, ChevronDown, Phone, Mail, Clock, Utensils, Wine, Waves,
  Info, Droplets, Fuel, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { earlyBirdInfo } from '../lib/earlyBird';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Photos hero par destination
const heroImages = {
  'tour-de-corse-croisiere-catamaran': 'https://static.wixstatic.com/media/ce6ce7_0ce032b8fe2e4bf58652c8d18e897478~mv2.jpg/v1/fill/w_1600,h_700,al_c,q_80,enc_avif,quality_auto/SOGNUDIMARE%20CROISIERE%20CATAMARAN%20DEPUIS%20AJACCIO%20copie.jpg',
  'croisiere-catamaran-corse-du-sud': 'https://static.wixstatic.com/media/ce6ce7_98fe44ee0ba0419794505afe2c70aa95~mv2.jpg/v1/fill/w_1600,h_700,al_c,q_80,enc_avif,quality_auto/SOGNUDIMARE%20CROISIERE%20CATAMARAN%20DEPUIS%20AJACCIO.jpg',
  'croisiere-catamaran-ouest-corse': 'https://static.wixstatic.com/media/ce6ce7_f3bee630d0c549388bbc71c0e58fb9ea~mv2.jpg/v1/fill/w_1600,h_700,al_c,q_80,enc_avif,quality_auto/sognudimare%20a%20scandola_edited.jpg',
  'croisiere-catamaran-sardaigne-corse': 'https://static.wixstatic.com/media/ce6ce7_9e15d4c9779b4d48ad56a689ab2fe02b~mv2.avif/v1/fill/w_1600,h_700,al_c,q_80,enc_avif,quality_auto/LAGOON%2043%20SOGNUDIMARE.avif',
  'croisiere-catamaran-grece-authentique': 'https://static.wixstatic.com/media/ce6ce7_227bd245ee9c48eca3a0f527080bf3a7~mv2.jpg/v1/fill/w_1600,h_700,al_c,q_80,enc_avif,quality_auto/cephalonie%20grece.jpg',
  'croisiere-catamaran-grece-cyclades-2-semaines': '/images/grece_cyclades_hero.jpg',
  'croisiere-catamaran-grenadines-caraibes': 'https://static.wixstatic.com/media/ce6ce7_3eb608d9c85e4effadf3d95d064cac02~mv2.webp/v1/fill/w_1600,h_700,al_c,q_80,enc_avif,quality_auto/grenadines-a-la-decouverte-des-grenadines_482185_panohd.webp',
};

// Thumbnails cartes embarquement
const boardingCards = {
  'tour-de-corse-croisiere-catamaran': 'https://static.wixstatic.com/media/ce6ce7_170fb96af2764aecb7eb7c526a48eb27~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20le%20tour%20de%20Corse%20sognudimare.png',
  'croisiere-catamaran-corse-du-sud': 'https://static.wixstatic.com/media/ce6ce7_2c02fe160efb49b6930f0c695a53e34f~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20la%20corse%20du%20sud%20sognudimare.png',
  'croisiere-catamaran-ouest-corse': 'https://static.wixstatic.com/media/ce6ce7_bdc5406402ea4be3b94eeeb747d2da1a~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20ouest%20corse%20sognudimare.png',
  'croisiere-catamaran-sardaigne-corse': 'https://static.wixstatic.com/media/ce6ce7_68a8fb4c934c44cb909dfc0075f36d83~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20la%20sardaigne%20et%20la%20corse%20du%20sud%20sognudimare.png',
  'croisiere-catamaran-grece-authentique': 'https://static.wixstatic.com/media/ce6ce7_95912f428d264151b75bce60481c048d~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20la%20grèce%20authentique%20sognudimare.png',
  'croisiere-catamaran-grece-cyclades-2-semaines': '/images/carte_embarquement_cyclades.png',
  'croisiere-catamaran-grenadines-caraibes': 'https://static.wixstatic.com/media/ce6ce7_748110d102c448599459ce93b281fdb6~mv2.png/v1/fill/w_200,h_150,q_85/croisiere%20catamaran%20les%20iles%20grenadines%20sognudimare.png',
};

const formatDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const startStr = start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const endStr = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${startStr} → ${endStr}`;
};

const statusConfig = {
  available: { label: 'Disponible', color: 'bg-emerald-500', textColor: 'text-emerald-600', dotColor: 'bg-emerald-500' },
  few_spots: { label: 'Quelques places', color: 'bg-orange-500', textColor: 'text-orange-600', dotColor: 'bg-orange-500' },
  full: { label: 'COMPLET', color: 'bg-red-500', textColor: 'text-red-500', dotColor: 'bg-red-500' },
};

const DayCard = ({ day, index, isOpen, onToggle }) => (
  <div 
    className="border border-slate-200 rounded-xl overflow-hidden transition-all duration-300 hover:border-[#2A9D8F]/40"
    data-testid={`day-card-${index}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-5 text-left hover:bg-[#F8F9FA] transition-colors"
    >
      <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#0A2540] rounded-xl flex flex-col items-center justify-center text-white shrink-0">
        <span className="text-[8px] sm:text-[10px] uppercase tracking-wider opacity-70">Jour</span>
        <span className="text-base sm:text-lg font-bold leading-tight">{day.day || index + 1}</span>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-display font-semibold text-[#0A2540] text-sm sm:text-base uppercase tracking-wide truncate">
          {day.title}
        </h4>
        {day.distance && (
          <span className="text-xs text-[#C4724F] font-medium">{day.distance}</span>
        )}
      </div>
      <ChevronDown className={`h-5 w-5 text-[#94A3B8] shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="px-3 sm:px-5 pb-4 sm:pb-5 animate-fade-in">
        <div className="sm:pl-[4.5rem] sm:border-l-2 sm:border-[#2A9D8F]/20 sm:ml-2">
          <p className="text-[#4A5568] leading-relaxed sm:pl-4 text-sm sm:text-base">{day.description}</p>
        </div>
      </div>
    )}
  </div>
);

const IncludedItem = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4 p-4 bg-white rounded-xl border border-slate-100">
    <div className="w-12 h-12 bg-[#2A9D8F]/10 rounded-xl flex items-center justify-center shrink-0">
      <Icon className="h-6 w-6 text-[#2A9D8F]" />
    </div>
    <div>
      <h4 className="font-semibold text-[#0A2540] text-sm">{title}</h4>
      <p className="text-[#94A3B8] text-xs mt-0.5">{description}</p>
    </div>
  </div>
);

const DepartureRow = ({ dep, isFirst, cruiseSlug, isOnRequest }) => {
  const cfg = statusConfig[dep.status] || statusConfig.available;
  const isFull = dep.status === 'full';
  const reserveLink = `/reservation-cabines-privatisation-catamaran-sognudimare?cruise=${cruiseSlug}&date=${dep.start_date}`;
  const cabinsLeft = dep.available_cabins;
  const eb = earlyBirdInfo(dep.start_date);
  const basePrice = dep.price_per_cabin;
  const discountedPrice = eb.eligible && basePrice ? Math.round(basePrice * 0.95) : null;
  const [pressed, setPressed] = useState(false);
  const navigate = useNavigate();
  const interactive = !isFull && !isOnRequest;

  const handleRowClick = (e) => {
    if (!interactive) return;
    // Avoid intercepting clicks on inner buttons/links
    if (e.target.closest('a, button')) return;
    setPressed(true);
    setTimeout(() => navigate(reserveLink), 220);
  };

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : -1}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (interactive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleRowClick(e);
        }
      }}
      className={`group relative p-3 sm:p-4 rounded-xl transition-all duration-300 ease-out
        ${interactive ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_rgba(10,37,64,0.18)] focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]/50' : ''}
        ${pressed ? 'scale-[0.985] shadow-inner' : ''}
        ${isFirst
          ? 'bg-[#2A9D8F]/5 border-2 border-[#2A9D8F]/30 hover:border-[#2A9D8F]/60'
          : eb.eligible
            ? 'bg-[#FFF8F2] border border-[#C4724F]/25 hover:border-[#C4724F]/60'
            : 'bg-white border border-slate-100 hover:border-[#2A9D8F]/35'}
        ${isFull ? 'opacity-60' : ''}`}
      data-testid={`departure-${dep.start_date}`}
    >
      {/* Subtle accent line that grows on hover */}
      {interactive && (
        <span
          aria-hidden
          className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-full origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-300 ${
            isFirst ? 'bg-[#2A9D8F]' : eb.eligible ? 'bg-[#C4724F]' : 'bg-[#2A9D8F]/70'
          }`}
        />
      )}

      <div className="flex items-center gap-3 sm:gap-4">
        <div className={`w-2.5 h-2.5 rounded-full ${cfg.dotColor} shrink-0 transition-transform duration-300 ${interactive ? 'group-hover:scale-125' : ''}`} />
        <div className="flex-1 min-w-0">
          <p className={`font-medium text-sm transition-colors ${isFull ? 'text-[#94A3B8]' : 'text-[#0A2540] group-hover:text-[#2A9D8F]'}`}>
            {formatDateRange(dep.start_date, dep.end_date)}
          </p>
          {isFirst && !isFull && <span className="text-xs text-[#2A9D8F] font-medium">Prochain départ</span>}
        </div>
        <div className="text-right shrink-0">
          {discountedPrice && !isFull ? (
            <>
              <div className="text-[10px] text-slate-400 line-through tabular-nums">
                {basePrice}€
              </div>
              <div className="text-lg font-display font-bold text-[#C4724F] tabular-nums leading-none transition-transform duration-300 group-hover:-translate-y-0.5">
                {discountedPrice}€
              </div>
            </>
          ) : (
            <span className={`text-lg font-display font-bold transition-transform duration-300 inline-block ${
              isFull ? 'text-[#94A3B8] line-through' : 'text-[#0A2540] group-hover:-translate-y-0.5 group-hover:text-[#2A9D8F]'
            }`}>
              {basePrice}€
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 pl-5 sm:pl-6 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-medium ${cfg.textColor}`}>{cfg.label}</span>
          {!isFull && cabinsLeft !== undefined && cabinsLeft <= 4 && (
            <CabinScarcity available={cabinsLeft} variant="compact" />
          )}
          {!isFull && eb.eligible && (
            <EarlyBirdBadge departureDate={dep.start_date} variant="inline" />
          )}
        </div>
        {!isFull && !isOnRequest && (
          <Button asChild size="sm" className="bg-[#C4724F] hover:bg-[#A0552F] text-white text-xs h-8 px-3 shrink-0 transition-all duration-300 group-hover:shadow-md group-hover:-translate-y-0.5">
            <Link to={reserveLink} onClick={(e) => e.stopPropagation()}>
              <span className="flex items-center gap-1">Réserver <ChevronRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" /></span>
            </Link>
          </Button>
        )}
        {!isFull && isOnRequest && (
          <Button asChild size="sm" variant="outline" className="border-[#2A9D8F] text-[#2A9D8F] text-xs h-8 px-3 shrink-0">
            <a href={`mailto:contact@sognudimare-catamarans.com?subject=Demande de réservation - ${cruiseSlug}`} onClick={(e) => e.stopPropagation()}>Contacter</a>
          </Button>
        )}
      </div>
    </div>
  );
};

const CruiseDetailPage = ({ cruiseSlug }) => {
  const [cruise, setCruise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDays, setOpenDays] = useState({});
  const [allCruises, setAllCruises] = useState([]);
  const [siblingCruises, setSiblingCruises] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cruiseRes, allRes] = await Promise.all([
          axios.get(`${API}/cruises/${cruiseSlug}`),
          axios.get(`${API}/cruises`)
        ]);
        setCruise(cruiseRes.data);
        setAllCruises(allRes.data.filter(c => c.slug !== cruiseSlug));
        // Find sibling cruise (same destination, different duration)
        const dest = cruiseRes.data.destination?.toLowerCase() || '';
        const siblings = allRes.data.filter(c => 
          c.slug !== cruiseSlug && 
          c.destination?.toLowerCase() === dest &&
          c.is_active
        );
        setSiblingCruises(siblings);
      } catch (err) {
        setError('Croisière non trouvee');
      } finally {
        setLoading(false);
      }
    };
    if (cruiseSlug) fetchData();
  }, [cruiseSlug]);

  const toggleDay = (index) => {
    setOpenDays(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const expandAll = () => {
    const all = {};
    cruise.itinerary.forEach((_, i) => { all[i] = true; });
    setOpenDays(all);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2A9D8F]"></div>
        </div>
      </Layout>
    );
  }

  if (error || !cruise) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#0A2540] mb-4">Croisière non trouvee</h1>
            <Button asChild>
              <Link to="/destinations-croisieres-catamaran">Voir toutes les croisières</Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const heroImg = heroImages[cruiseSlug] || cruise.images?.[0];
  const heroGallery = buildCruiseGallery(cruiseSlug);
  const sortedDates = (cruise.dates || []).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  const now = new Date();
  const futureDates = sortedDates.filter(d => new Date(d.start_date) > now);
  const nextAvailable = futureDates.find(d => d.status !== 'full');

  const includedFeatures = [
    { icon: Ship, title: "HEBERGEMENT INCLUS", description: "Cabine double tout confort avec salle d'eau et WC" },
    { icon: Utensils, title: "REPAS INCLUS", description: "Pension complète - produits locaux et circuits courts" },
    { icon: Wine, title: "BOISSONS INCLUSES", description: "Softs et alcools - vins issus de vignobles locaux" },
    { icon: Waves, title: "ACTIVITES INCLUSES", description: "Paddle, materiel de peche" },
    { icon: Users, title: "EQUIPAGE INCLUS", description: "Capitaine et hotesse cook professionnels" },
  ];

  return (
    <Layout>
      <SEO
        title={`${cruise?.name || 'Croisière Catamaran'} — ${cruise?.destination || 'Méditerranée'}`}
        description={`${cruise?.short_description || `Croisière catamaran SOGNUDIMARE en ${cruise?.destination || 'Corse et Méditerranée'}`} — Pension complète avec équipage, départ ${cruise?.departure_port || 'Corse'}. Réservation à la cabine ou privatisation Lagoon 46/43.`}
        path={`/${cruiseSlug}`}
        type="article"
        image={heroImg}
        keywords={[
          `croisière catamaran ${(cruise?.destination || '').toLowerCase()}`,
          `croisière catamaran ${(cruise?.departure_port || '').toLowerCase()}`,
          'croisière catamaran avec équipage',
          'croisière catamaran pension complète',
          'cabine catamaran',
          'privatisation catamaran',
          'lagoon 46',
          'lagoon 43',
          'lagoon 38',
          `${cruise?.duration || ''} catamaran`,
          'slow tourisme',
          'sognudimare',
        ].filter(Boolean)}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'TouristTrip',
            name: cruise?.name,
            description: cruise?.description || cruise?.short_description,
            image: heroImg,
            touristType: ['Couples', 'Families', 'Luxury travelers', 'Slow tourism'],
            itinerary: {
              '@type': 'ItemList',
              numberOfItems: (cruise?.itinerary || []).length,
              itemListElement: (cruise?.itinerary || []).map((d, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                item: { '@type': 'Place', name: d?.title || `Jour ${i + 1}`, description: d?.description },
              })),
            },
            provider: { '@type': 'TravelAgency', name: 'SOGNUDIMARE', url: 'https://sognudimare.com' },
            offers: nextAvailable
              ? {
                  '@type': 'Offer',
                  price: String(nextAvailable.price_per_cabin || cruise?.base_price || cruise?.privatization_base_price || 0),
                  priceCurrency: 'EUR',
                  availability: 'https://schema.org/InStock',
                  validFrom: nextAvailable.start_date,
                  url: `https://sognudimare.com/reservation-cabines-privatisation-catamaran-sognudimare?cruise=${cruiseSlug}`,
                }
              : undefined,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://sognudimare.com/' },
              { '@type': 'ListItem', position: 2, name: 'Destinations', item: 'https://sognudimare.com/destinations-croisieres-catamaran' },
              { '@type': 'ListItem', position: 3, name: cruise?.name, item: `https://sognudimare.com/${cruiseSlug}` },
            ],
          },
        ]}
      />
      {/* Hero — triptych carousel (3 photos visible at once, preserving ratio) */}
      <div className="pt-20 bg-[#0A2540]" data-testid="cruise-detail-hero">
        <CruiseHeroCarousel photos={heroGallery}>
          <div className="max-w-[1500px] mx-auto flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 sm:gap-8">
            {/* LEFT: titles + meta */}
            <div className="flex-1 min-w-0">
              <div className="hidden sm:flex items-center gap-2 text-white/70 text-sm mb-3" data-testid="breadcrumb">
                <Link to="/" className="hover:text-white">Accueil</Link>
                <ChevronRight className="h-4 w-4" />
                <Link to="/destinations-croisieres-catamaran" className="hover:text-white">Destinations</Link>
                <ChevronRight className="h-4 w-4" />
                <span className="text-white">{cruise.destination}</span>
              </div>
              <p className="text-[#3AB8A8] font-medium text-xs sm:text-sm uppercase tracking-wider mb-2">
                Croisière catamaran authentique
              </p>
              <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4" data-testid="cruise-title">
                {cruise.name}
              </h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-white/90 text-sm">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-[#3AB8A8]" />
                  <span>{cruise.duration}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Ship className="h-4 w-4 text-[#3AB8A8]" />
                  <span className="hidden sm:inline">Départ:</span> {cruise.départure_port || cruise.departure_port}
                </div>
                <div className="flex items-center gap-1.5">
                  <Users className="h-4 w-4 text-[#3AB8A8]" />
                  <span>{cruise.max_passengers || 8} passagers</span>
                </div>
                <div className="hidden sm:flex items-center gap-1.5">
                  <Utensils className="h-4 w-4 text-[#3AB8A8]" />
                  <span>Pension complète</span>
                </div>
              </div>
            </div>

            {/* RIGHT: stylized boarding card */}
            {boardingCards[cruiseSlug] && (
              <div className="shrink-0 self-end sm:self-auto" data-testid="hero-boarding-card">
                <div
                  className="relative group cursor-pointer"
                  style={{ perspective: '1200px' }}
                >
                  <div
                    className="relative transform rotate-[-2.5deg] group-hover:rotate-0 group-hover:scale-[1.05] transition-all duration-700 ease-out"
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    {/* Glow halo behind */}
                    <div className="absolute -inset-3 bg-gradient-to-br from-[#C4724F]/50 via-[#3AB8A8]/35 to-[#C4956A]/55 rounded-3xl blur-2xl opacity-70 group-hover:opacity-100 transition-opacity duration-700" />

                    {/* The boarding card image */}
                    <div className="relative rounded-xl overflow-hidden border border-white/15 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.85)]">
                      <img
                        src={boardingCards[cruiseSlug].replace('/w_200,h_150', '/w_600,h_450')}
                        alt={`Carte d'embarquement ${cruise.name}`}
                        className="w-[230px] sm:w-[280px] h-auto block"
                        loading="lazy"
                        decoding="async"
                      />
                      {/* Holographic shimmer on hover */}
                      <div
                        className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                        style={{
                          background:
                            'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)',
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CruiseHeroCarousel>
      </div>

      {/* Quick Action Bar */}
      <section className="bg-white border-b border-slate-100 sticky top-[56px] z-30" data-testid="action-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <div className="hidden sm:flex items-center gap-4 md:gap-6 text-sm overflow-x-auto">
              <a href="#departs" className="font-medium text-[#0A2540] hover:text-[#2A9D8F] whitespace-nowrap transition-colors">Départs</a>
              <a href="#programme" className="font-medium text-[#0A2540] hover:text-[#2A9D8F] whitespace-nowrap transition-colors">Programme</a>
              <a href="#tarifs" className="font-medium text-[#0A2540] hover:text-[#2A9D8F] whitespace-nowrap transition-colors">Tarifs</a>
              <a href="#inclus" className="font-medium text-[#0A2540] hover:text-[#2A9D8F] whitespace-nowrap transition-colors">Tout inclus</a>
              <a href="#autres" className="hidden md:block font-medium text-[#0A2540] hover:text-[#2A9D8F] whitespace-nowrap transition-colors">Autres destinations</a>
            </div>
            {/* Mobile: show price + CTA */}
            <div className="flex sm:hidden items-center gap-3 flex-1">
              {!cruise.is_on_request && nextAvailable && (
                <span className="text-lg font-display font-bold text-[#C4724F]">{nextAvailable.price_per_cabin}€<span className="text-xs text-[#94A3B8] font-normal">/pers</span></span>
              )}
            </div>
            <div className="shrink-0">
              {cruise.is_on_request ? (
                <Button asChild className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white font-semibold text-sm" size="sm" data-testid="action-bar-contact-btn">
                  <a href="mailto:contact@sognudimare-catamarans.com">
                    <Mail className="mr-1.5 h-4 w-4" /> <span className="hidden sm:inline">Nous </span>contacter
                  </a>
                </Button>
              ) : (
                <Button asChild className="bg-[#C4724F] hover:bg-[#C4956A] text-white font-semibold text-sm" size="sm" data-testid="action-bar-reserve-btn">
                  <Link to="/reservation-cabines-privatisation-catamaran-sognudimare">
                    Réserver <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Prochain départ highlight + Description */}
      <section className="py-12 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {cruise.is_on_request && (
            <div className="mb-8 p-6 bg-[#0A2540] rounded-2xl shadow-sm" data-testid="on-request-banner">
              <div className="flex items-center gap-2 text-[#C4724F] font-medium text-xs uppercase tracking-wider mb-2">
                <Mail className="h-4 w-4" />
                Sur demande uniquement
              </div>
              <p className="text-white text-lg font-display font-bold mb-2">
                Cette destination est disponible à la demande
              </p>
              <p className="text-white/70 text-sm mb-4">
                {cruiseSlug === 'croisiere-catamaran-grece-authentique' 
                  ? 'Départs de mai a fin septembre. '
                  : cruiseSlug === 'croisiere-catamaran-grenadines-caraibes'
                  ? 'Départs de novembre a mars. '
                  : ''}
                Contactez-nous par email pour indiquer vos dates souhaitées et recevoir un devis personnalisé.
              </p>
              <Button asChild className="bg-[#C4724F] hover:bg-[#C4724F]/90 text-white font-semibold">
                <a href="mailto:contact@sognudimare-catamarans.com?subject=Demande de devis - {cruise.name}">
                  <Mail className="mr-2 h-4 w-4" /> Demander un devis par email
                </a>
              </Button>
            </div>
          )}
          {!cruise.is_on_request && nextAvailable && (
            <div className="mb-8 p-4 sm:p-6 bg-white rounded-2xl border-2 border-[#2A9D8F]/20 shadow-sm" data-testid="next-departure-highlight">
              <div className="flex items-center gap-2 text-[#2A9D8F] font-medium text-xs uppercase tracking-wider mb-2">
                <Clock className="h-4 w-4" />
                Prochain départ
              </div>
              <p className="text-lg sm:text-xl font-display font-bold text-[#0A2540] mb-3">
                {formatDateRange(nextAvailable.start_date, nextAvailable.end_date)}
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <span className="text-2xl sm:text-3xl font-display font-bold text-[#C4724F]">{nextAvailable.price_per_cabin}€</span>
                  <span className="text-sm text-[#94A3B8] ml-2">par personne</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${statusConfig[nextAvailable.status]?.dotColor || 'bg-emerald-500'}`} />
                  <span className={`text-sm font-medium ${statusConfig[nextAvailable.status]?.textColor || 'text-emerald-600'}`}>
                    {statusConfig[nextAvailable.status]?.label || 'Disponible'}
                    {nextAvailable.available_cabins < 4 && ` (${nextAvailable.available_cabins} cab.)`}
                  </span>
                </div>
                <Button asChild className="bg-[#C4724F] hover:bg-[#C4724F]/90 text-white font-semibold w-full sm:w-auto">
                  <Link to={`/reservation-cabines-privatisation-catamaran-sognudimare?cruise=${cruiseSlug}&date=${nextAvailable.start_date}`}>
                    Réserver <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          )}
          <p className="text-[#4A5568] text-lg leading-relaxed text-center" data-testid="cruise-description">
            {cruise.description}
          </p>
          {cruise.highlights && cruise.highlights.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {cruise.highlights.map((h, i) => (
                <span key={i} className="px-4 py-2 bg-white rounded-full text-sm font-medium text-[#0A2540] border border-slate-200">{h}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Tous les départs - only for non-on-request cruises */}
      {!cruise.is_on_request && futureDates.length > 0 && (
        <section id="departs" className="py-16 bg-white" data-testid="departures-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2540] mb-3 uppercase tracking-wide" data-testid="departures-title">
                Réservez votre prochaine croisière
              </h2>
              <p className="text-[#4A5568] uppercase tracking-wider text-sm font-medium" data-testid="departures-subtitle">
                À la cabine ou en privatisation — Avec équipage et pension complète
              </p>
            </div>

            {/* Scarcity alert: count departures with limited availability */}
            {(() => {
              const lowAvail = futureDates.filter(
                (d) => d.status !== 'full' && d.available_cabins !== undefined && d.available_cabins <= 2
              ).length;
              if (lowAvail === 0) return null;
              return (
                <div
                  className="mb-6 flex items-center gap-3 sm:gap-4 p-4 rounded-2xl bg-gradient-to-r from-orange-50 via-red-50 to-orange-50 border border-orange-200"
                  data-testid="scarcity-alert"
                >
                  <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-md">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-90" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm sm:text-base font-bold text-[#7C2D12] leading-tight">
                      {lowAvail === 1 ? 'Un départ presque complet' : `${lowAvail} départs presque complets`}
                    </div>
                    <div className="text-xs sm:text-sm text-[#9A3412]">
                      Profitez vite des dernières cabines disponibles ci-dessous.
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="space-y-3">
              {futureDates.map((dep, i) => (
                <DepartureRow key={dep.start_date} dep={dep} isFirst={dep === nextAvailable} cruiseSlug={cruiseSlug} isOnRequest={cruise.is_on_request} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      <section id="tarifs" className="py-16 bg-[#F8F9FA]" data-testid="tarifs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-[#0A2540] text-center mb-4">
            Vous souhaitez réserver votre place ou privatiser le catamaran
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10 max-w-5xl mx-auto">
            {cruise.is_cabin_booking && cruise.base_price > 0 && (
              <Card className="overflow-hidden border-2 border-slate-200 hover:border-[#2A9D8F] transition-colors" data-testid="cabin-pricing">
                <CardContent className="p-0">
                  <div className="bg-[#F8F9FA] p-6">
                    <h3 className="font-display text-xl font-bold text-[#0A2540] uppercase">Réservation a la cabine</h3>
                    <p className="text-sm text-[#4A5568] mt-1">en pension complète avec équipage</p>
                  </div>
                  <div className="p-6">
                    <div className="text-center mb-6">
                      <span className="text-sm text-[#94A3B8]">à partir de</span>
                      <div className="text-4xl font-display font-bold text-[#C4724F]">{cruise.base_price}€</div>
                      <span className="text-sm text-[#94A3B8]">par passager *</span>
                    </div>
                    <ul className="space-y-2 text-sm text-[#4A5568] mb-6">
                      <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#2A9D8F] mt-0.5 shrink-0" /> Cabine double pour 2 passagers</li>
                      <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#2A9D8F] mt-0.5 shrink-0" /> Salle d'eau et WC independants</li>
                      <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#2A9D8F] mt-0.5 shrink-0" /> Pension complète toute la durée</li>
                      <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#2A9D8F] mt-0.5 shrink-0" /> Hotesse cook + Capitaine</li>
                    </ul>
                    <div className="bg-[#FFF8F0] border border-[#C4724F]/20 rounded-lg p-3 mb-4 cursor-pointer group relative" onClick={(e) => { const el = e.currentTarget.querySelector('.caisse-detail'); if (el) el.classList.toggle('hidden'); }}>
                      <p className="text-xs font-semibold text-[#C4724F] flex items-center gap-1">+ Caisse de bord <Info className="h-3 w-3 opacity-60" /></p>
                      <p className="text-sm text-[#4A5568] mt-1">{cruise.duration?.includes('14') ? '500' : '250'}€ par passager {cruise.duration?.includes('14') ? '(2 semaines)' : '(1 semaine)'}</p>
                      <div className="caisse-detail hidden mt-3 pt-3 border-t border-[#C4724F]/10 text-xs text-[#4A5568] space-y-1.5">
                        <p className="font-semibold text-[#0A2540] text-xs mb-1">La caisse de bord comprend (obligatoire) :</p>
                        <p className="flex items-start gap-2"><Anchor className="h-3 w-3 text-[#C4724F] mt-0.5 shrink-0" /><span><strong>Frais de port</strong> — Amarrage et mouillage dans les differents ports</span></p>
                        <p className="flex items-start gap-2"><Droplets className="h-3 w-3 text-[#2A9D8F] mt-0.5 shrink-0" /><span><strong>Frais d'eau</strong> — Consommation d'eau a bord</span></p>
                        <p className="flex items-start gap-2"><Fuel className="h-3 w-3 text-[#D4AF37] mt-0.5 shrink-0" /><span><strong>Frais d'energies</strong> — Gasoil, gaz et essence pour le voilier</span></p>
                        <p className="flex items-start gap-2"><Sparkles className="h-3 w-3 text-[#94A3B8] mt-0.5 shrink-0" /><span><strong>Nettoyage</strong> — Nettoyage du voilier en fin de croisiere</span></p>
                      </div>
                    </div>
                    {cruise.is_on_request ? (
                      <Button asChild className="w-full bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white font-semibold" size="lg" data-testid="contact-cabin-btn">
                        <a href="mailto:contact@sognudimare-catamarans.com?subject=Demande de devis cabine">
                          <Mail className="mr-2 h-5 w-5" /> Demander un devis
                        </a>
                      </Button>
                    ) : (
                      <Button asChild className="w-full bg-[#C4724F] hover:bg-[#C4956A] text-white font-semibold" size="lg" data-testid="reserve-cabin-btn">
                        <Link to="/reservation-cabines-privatisation-catamaran-sognudimare">
                          Choisir votre place <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            {cruise.is_privatization && (
              <Card className="overflow-hidden border-2 border-[#0A2540]" data-testid="privatization-pricing">
                <CardContent className="p-0">
                  <div className="bg-[#0A2540] p-6">
                    <h3 className="font-display text-xl font-bold text-white uppercase">Privatiser le catamaran</h3>
                    <p className="text-sm text-white/70 mt-1">en pension complète avec équipage</p>
                  </div>
                  <div className="p-6">
                    <div className="text-center mb-6">
                      <span className="text-sm text-[#94A3B8]">à partir de</span>
                      <div className="text-4xl font-display font-bold text-[#0A2540]">{cruise.privatization_base_price}€</div>
                      <span className="text-sm text-[#94A3B8]">pour le catamaran</span>
                    </div>
                    <ul className="space-y-2 text-sm text-[#4A5568] mb-6">
                      <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#2A9D8F] mt-0.5 shrink-0" /> Privatisation de toutes les cabines ({cruise.max_passengers || 8} passagers)</li>
                      <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#2A9D8F] mt-0.5 shrink-0" /> Salle d'eau et WC independants par cabine</li>
                      <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#2A9D8F] mt-0.5 shrink-0" /> Pension complète toute la durée</li>
                      <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#2A9D8F] mt-0.5 shrink-0" /> Hotesse cook + Capitaine</li>
                    </ul>
                    <div className="bg-[#F0F4F8] border border-[#0A2540]/10 rounded-lg p-3 mb-4 cursor-pointer group relative" onClick={(e) => { const el = e.currentTarget.querySelector('.caisse-detail'); if (el) el.classList.toggle('hidden'); }}>
                      <p className="text-xs font-semibold text-[#0A2540] flex items-center gap-1">+ Caisse de bord <Info className="h-3 w-3 opacity-60" /></p>
                      <p className="text-sm text-[#4A5568] mt-1">{cruise.duration?.includes('14') ? '500' : '250'}€ par passager {cruise.duration?.includes('14') ? '(2 semaines)' : '(1 semaine)'}</p>
                      <div className="caisse-detail hidden mt-3 pt-3 border-t border-[#0A2540]/10 text-xs text-[#4A5568] space-y-1.5">
                        <p className="font-semibold text-[#0A2540] text-xs mb-1">La caisse de bord comprend (obligatoire) :</p>
                        <p className="flex items-start gap-2"><Anchor className="h-3 w-3 text-[#C4724F] mt-0.5 shrink-0" /><span><strong>Frais de port</strong> — Amarrage et mouillage dans les differents ports</span></p>
                        <p className="flex items-start gap-2"><Droplets className="h-3 w-3 text-[#2A9D8F] mt-0.5 shrink-0" /><span><strong>Frais d'eau</strong> — Consommation d'eau a bord</span></p>
                        <p className="flex items-start gap-2"><Fuel className="h-3 w-3 text-[#D4AF37] mt-0.5 shrink-0" /><span><strong>Frais d'energies</strong> — Gasoil, gaz et essence pour le voilier</span></p>
                        <p className="flex items-start gap-2"><Sparkles className="h-3 w-3 text-[#94A3B8] mt-0.5 shrink-0" /><span><strong>Nettoyage</strong> — Nettoyage du voilier en fin de croisiere</span></p>
                      </div>
                    </div>
                    {cruise.is_on_request ? (
                      <Button asChild className="w-full bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white font-semibold" size="lg" data-testid="contact-private-btn">
                        <a href="mailto:contact@sognudimare-catamarans.com?subject=Demande de privatisation">
                          <Mail className="mr-2 h-5 w-5" /> Demander un devis
                        </a>
                      </Button>
                    ) : (
                      <Button asChild className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white font-semibold" size="lg" data-testid="reserve-private-btn">
                        <Link to="/reservation-cabines-privatisation-catamaran-sognudimare">
                          Privatiser en exclusivité <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Duration Selector - Show if sibling cruises exist */}
      {siblingCruises.length > 0 && (
        <section className="py-8 bg-[#0A2540]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-white/60 text-sm mb-4">Choisissez votre formule</p>
            <div className="inline-flex bg-white/10 rounded-xl p-1 gap-1">
              <button
                className="px-5 sm:px-8 py-3 rounded-lg font-bold text-sm transition-all bg-[#D4AF37] text-white shadow-lg"
                data-testid="duration-current"
              >
                {cruise.duration} — {cruise.privatization_base_price > 0 ? `${new Intl.NumberFormat('fr-FR').format(cruise.privatization_base_price)} €` : 'Sur demande'}
              </button>
              {siblingCruises.map(s => (
                <a
                  key={s.slug}
                  href={`/${s.slug}`}
                  className="px-5 sm:px-8 py-3 rounded-lg font-bold text-sm transition-all text-white/70 hover:bg-white/10 hover:text-white"
                  data-testid={`duration-sibling-${s.slug}`}
                >
                  {s.duration} — {s.privatization_base_price > 0 ? `${new Intl.NumberFormat('fr-FR').format(s.privatization_base_price)} €` : 'Sur demande'}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Programme */}
      {cruise.itinerary && cruise.itinerary.length > 0 && (
        <section id="programme" className="py-16 bg-white" data-testid="programme-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display text-3xl font-bold text-[#0A2540] mb-3">Programme detaille de la croisière</h2>
              <p className="text-[#4A5568]">L'itineraire peut varier en fonction des conditions meteorologiques et des envies de l'équipage et des passagers.</p>
              <button onClick={expandAll} className="mt-4 text-sm text-[#2A9D8F] hover:text-[#2A9D8F]/80 font-medium underline underline-offset-4" data-testid="expand-all-btn">
                Tout afficher
              </button>
            </div>
            <div className="space-y-3">
              {cruise.itinerary.map((day, i) => (
                <DayCard key={i} day={day} index={i} isOpen={!!openDays[i]} onToggle={() => toggleDay(i)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Inclusive */}
      <section id="inclus" className="py-16 bg-[#F8F9FA]" data-testid="inclus-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl font-bold text-[#0A2540] mb-3">Des vacances tout inclus</h2>
            <p className="text-[#4A5568]">Formule tout compris & respectueuse de l'environnement</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {includedFeatures.map((feature, i) => (
              <IncludedItem key={i} {...feature} />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            <div>
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mb-4 flex items-center gap-2">
                <div className="w-7 h-7 bg-[#2A9D8F] rounded-full flex items-center justify-center"><Check className="h-4 w-4 text-white" /></div>
                Ce qui est inclus
              </h3>
              <ul className="space-y-2">
                {(cruise.included || []).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#4A5568]"><Check className="h-4 w-4 text-[#2A9D8F] mt-0.5 shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mb-4 flex items-center gap-2">
                <div className="w-7 h-7 bg-red-400 rounded-full flex items-center justify-center"><X className="h-4 w-4 text-white" /></div>
                Non inclus
              </h3>
              <ul className="space-y-2">
                {(cruise.not_included || []).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#4A5568]"><X className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Club CTA */}
      <section className="py-12 bg-[#0A2540]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
            <div className="text-center sm:text-left">
              <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-2">Devenez membre du Club des Voyageurs</h3>
              <p className="text-white/70 text-sm sm:text-base">Profitez de remises exceptionnelles de 10 a 20% sur votre future croisière</p>
            </div>
            <Button asChild className="bg-[#C4724F] hover:bg-[#C4956A] text-white font-semibold whitespace-nowrap w-full sm:w-auto" size="lg" data-testid="club-cta-btn">
              <Link to="/club">Rejoindre le Club <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Other Cruises with boarding card thumbnails */}
      {allCruises.length > 0 && (
        <section id="autres" className="py-16 bg-[#F8F9FA]" data-testid="other-cruises-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl font-bold text-[#0A2540] text-center mb-10">Autres propositions de croisières en catamaran</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {allCruises.map((c) => (
                <Link key={c.slug} to={`/${c.slug}`} className="group text-center p-3 sm:p-4 bg-white rounded-xl border border-slate-100 hover:border-[#2A9D8F] hover:shadow-md transition-all" data-testid={`other-cruise-${c.slug}`}>
                  {boardingCards[c.slug] ? (
                    <img src={boardingCards[c.slug]} alt={c.name} className="w-24 h-16 object-cover rounded-lg mx-auto mb-3 group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-16 h-16 bg-[#0A2540] rounded-full flex items-center justify-center mx-auto mb-3">
                      <Anchor className="h-7 w-7 text-[#2A9D8F]" />
                    </div>
                  )}
                  <h4 className="font-display font-semibold text-[#0A2540] text-sm group-hover:text-[#2A9D8F] transition-colors uppercase">{c.destination}</h4>
                  <p className="text-xs text-[#94A3B8] mt-1">{c.duration}</p>
                  <p className="text-xs text-[#C4724F] mt-1 font-semibold">{c.base_price > 0 ? `${c.base_price}€/pers` : `${c.privatization_base_price}€`}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-display text-2xl font-bold text-[#0A2540] mb-4">Des questions ? Contactez votre capitaine</h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
            <a href="tel:0495729028" className="flex items-center gap-2 text-[#0A2540] hover:text-[#2A9D8F] font-medium" data-testid="contact-phone">
              <Phone className="h-5 w-5" /> 04 95 72 90 28
            </a>
            <a href="mailto:contact@sognudimare-catamarans.com" className="flex items-center gap-2 text-[#0A2540] hover:text-[#2A9D8F] font-medium" data-testid="contact-email">
              <Mail className="h-5 w-5" /> contact@sognudimare-catamarans.com
            </a>
          </div>
          <Button asChild className="bg-[#C4724F] hover:bg-[#C4956A] text-white font-semibold" size="lg" data-testid="final-reserve-btn">
            <Link to="/reservation-cabines-privatisation-catamaran-sognudimare">
              Réserver maintenant <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default CruiseDetailPage;
